import { Injectable, InjectionToken, inject } from '@angular/core';
import {
  LoqateApiError,
  LoqateBankValidationApi,
} from '@fmnts/api/vendors/loqate';
import { isNonEmptyString } from '@fmnts/core';
import { SortCode, checkSortCodeValidity } from '@formunauts/banking/domain';
import { Observable, catchError, map, of, timeout } from 'rxjs';
import { SORT_CODE_WEIGHT_TABLE } from './banking-data-acess.tokens';

export enum SortCodeValidity {
  Valid,
  Invalid,
  SortCodeInvalid,
  AccountNumberInvalid,
}

interface ValidateUseCaseConfig {
  online: {
    /** Timeout for online validation */
    timeout: number;
  };
}
const ValidateUseCaseConfigToken = new InjectionToken<ValidateUseCaseConfig>(
  '@formunauts/next/forms/payments/l10n/uk/validate/sort-code',
  {
    factory: () => ({
      online: {
        timeout: 5_000,
      },
    }),
  },
);

@Injectable()
export class SortCodeService {
  private readonly loqateValidationService = inject(LoqateBankValidationApi);
  private readonly useCaseConfig = inject(ValidateUseCaseConfigToken);

  readonly isSortCodeValid = checkSortCodeValidity(
    inject(SORT_CODE_WEIGHT_TABLE),
  );

  /**
   * Format a String to Sortcode
   *
   * @param sortCodeStr
   * @returns
   */
  public fromString(
    sortCodeStr: string,
    format: 'electronic' | 'print' = 'electronic',
  ): SortCode.LegacySortCode {
    return format === 'print'
      ? SortCode.LegacySortCode.parse(sortCodeStr)
      : new SortCode.LegacySortCode(sortCodeStr);
  }

  /**
   * Validates Payment Data of Sort-Code-Payment
   * online: the validation is done by loqate
   * offline: cached valacdos file does the verification
   * @param accountNumber
   * @param sortCode
   *
   * @returns
   * `Valid` when both SortCode and AccountNumber are correct
   * `SortCodeInvalid` when SortCode is incorrect
   * `AccountNumberInvalid` when the AccountNumber is not valid
   * `Invalid` when both are not valid (offline location)
   */
  validate(
    accountNumber: string,
    sortCode: string,
  ): Observable<SortCodeValidity> {
    // TODO(use-case): This should be implemented as a use case, as some parts of
    // this validation are specific to the usage.
    return this.onlineValidation(accountNumber, sortCode).pipe(
      timeout({ first: this.useCaseConfig.online.timeout }),
      catchError(() => of(this.offlineValidation(accountNumber, sortCode))),
    );
  }

  onlineValidation(
    accountNumber: string,
    sortCode: string,
  ): Observable<SortCodeValidity> {
    if (!sortCode) {
      return of(SortCodeValidity.SortCodeInvalid);
    }
    // Early exit instead of sending request that will yield an error
    if (!isNonEmptyString(accountNumber)) {
      return of(SortCodeValidity.Valid);
    }

    return this.loqateValidationService
      .validate({ sortCode, accountNumber })
      .pipe(
        map((response) => {
          if (response.IsCorrect) {
            return SortCodeValidity.Valid;
          }
          if (response.StatusInformation === 'UnknownSortCode') {
            return SortCodeValidity.SortCodeInvalid;
          }
          if (response.StatusInformation === 'InvalidAccountNumber') {
            return SortCodeValidity.AccountNumberInvalid;
          }

          return SortCodeValidity.Invalid;
        }),
        catchError((error) => {
          if (error instanceof LoqateApiError) {
            // Error Code if AccountNumber is missing
            if (error.code === '1003') {
              return of(SortCodeValidity.Valid);
            }
          }
          throw error;
        }),
      );
  }

  offlineValidation(accountNumber: string, sortCode: string): SortCodeValidity {
    if (!accountNumber || !sortCode) {
      return SortCodeValidity.Invalid;
    }

    return this.isSortCodeValid({ sortCode, accountNumber })
      ? SortCodeValidity.Valid
      : SortCodeValidity.Invalid;
  }
}
